import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

export default function Thoughts() {
  const [thoughts, setThoughts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/thoughts")
      .then((r) => setThoughts(r.data))
      .catch(() => setThoughts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main data-testid="thoughts-page" className="max-w-4xl mx-auto px-6 md:px-10 py-20 md:py-28">
      <Seo
        title="Thoughts"
        description="Miscellaneous thoughts on money and related fields — a running commonplace book of quotes and short reflections."
        path="/thoughts"
      />
      <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60 mb-4">
        Thoughts
      </div>
      <h1 className="font-serif text-5xl md:text-7xl font-light leading-[1.02] tracking-tighter">
        Miscellaneous thoughts on money
        <br />
        <span className="italic text-primary">and related fields.</span>
      </h1>
      <p className="mt-8 max-w-2xl text-lg text-foreground/70 leading-relaxed">
        Dated micro-entries. Quotes I keep returning to, and the small reflections
        they trigger. Ordered like a journal — read top-down.
      </p>

      <div className="mt-16 md:mt-20 space-y-16 md:space-y-20 border-t border-border/60 pt-14">
        {loading && (
          <div data-testid="thoughts-loading" className="text-foreground/60">
            Loading…
          </div>
        )}
        {!loading && thoughts.length === 0 && (
          <div
            data-testid="thoughts-empty"
            className="border border-dashed border-border rounded-sm py-16 px-8 text-center"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/50">
              The journal awaits
            </div>
            <p className="mt-4 font-serif text-2xl text-foreground/70 italic">
              No entries yet — the first thought is coming.
            </p>
          </div>
        )}
        {thoughts.map((t) => (
          <ThoughtEntry key={t.thought_id} t={t} />
        ))}
      </div>
    </main>
  );
}

function ThoughtEntry({ t }) {
  const date = formatEntryDate(t.entry_date);
  return (
    <article
      data-testid={`thought-${t.thought_id}`}
      className="animate-fade-up"
    >
      <div className="text-sm md:text-base font-body tracking-wide text-foreground/70">
        {date}
      </div>
      {t.quote && (
        <blockquote className="mt-6 border-l-2 border-primary pl-6 md:pl-8 font-serif text-2xl md:text-3xl italic leading-snug text-foreground/90 max-w-3xl">
          “{t.quote}”
          {t.quote_source && (
            <span className="block mt-3 not-italic text-xs uppercase tracking-widest text-foreground/50">
              — {t.quote_source}
            </span>
          )}
        </blockquote>
      )}
      {t.reflection && (
        <p className="mt-6 text-lg md:text-xl leading-[1.75] text-foreground/85 max-w-3xl">
          {t.reflection}
        </p>
      )}
    </article>
  );
}

function formatEntryDate(iso) {
  if (!iso) return "";
  const parts = iso.includes(".") ? iso.split(".").reverse() : iso.split("-");
  const d = new Date(`${parts[0]}-${parts[1] || "01"}-${parts[2] || "01"}`);
  if (isNaN(d)) return iso;
  return d
    .toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })
    .replace(/\//g, ".");
}
